import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import sortBy from 'lodash.sortby';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { updateState, getChipsLabel, createUpdatedChips, isChipActive, hasActiveChips } from '@helpers/filters';
import { formatTimeframeChipValue } from '@helpers/dates';
import { AccIntegrationName, ProfileName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { INCLUDE_COVER_MANAGER, INCLUDE_RECOVERY_FORM } from '@constants/feedbacks';
import { useTranslation, useSurveysOptions, useLocations } from '@hooks';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    className,
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    locations,
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
    applyChipsSignal = 0,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const account = useSelector(state => state.account?.account);
    const language = useSelector(state => state.auth.user?.language);
    const [filterChips, setFilterChips] = useState({
        companies: [],
        surveys: [],
        rating: [],
        statuses: [],
        timeframe: {},
        hideAnonimous: false,
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const { getBrandSurveysOptionsByIds, getSurveysByLocationIds } = useSurveysOptions();
    const { getLocationsOptions } = useLocations();
    const isTablet = useMediaQuery('@media (max-width: 1024px)');
    const isMobile = useMediaQuery('@media (max-width: 768px)');

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            applyChips();
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.companies.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    useEffect(() => {
        if (applyChipsSignal > 0) {
            applyChips();
        }
    }, [applyChipsSignal]);

    const isCoverManagerConnected = isIntegrationConnected(account, AccIntegrationName.CoverManager);

    const coverManagerConnectedCompanies = useMemo(
        () => locations?.filter(c => c.profiles.some(p => p.type === ProfileName.CoverManager))?.map(i => i.companyId),
        [locations],
    );

    const options = useMemo(() => {
        //locationsOptions
        const locationsOptions = getLocationsOptions();

        //surveysOptions
        let surveysOptions = getBrandSurveysOptionsByIds(filter.companies.map(item => item.id));
        const isCoverManagerInCompanies = filter.companies.some(item =>
            coverManagerConnectedCompanies.includes(item.id),
        );
        const surveysOptionsByCoverManager =
            isCoverManagerConnected && isCoverManagerInCompanies
                ? [...surveysOptions, { value: INCLUDE_COVER_MANAGER, label: 'CoverManager' }]
                : surveysOptions;
        surveysOptions = sortBy(
            [...surveysOptionsByCoverManager, { value: INCLUDE_RECOVERY_FORM, label: t('common.recoveryForm') }],
            ['label'],
        );

        //statusesOptions
        const statusesOptions = [
            { value: 0, label: t('common.none') },
            { value: 1, label: t('common.actionRequired') },
            { value: 2, label: t('common.inProgress') },
            { value: 3, label: t('FeedbackResponses.completed') },
        ];

        //ratingOptions
        const ratingOptions = [
            { value: 1, label: '1' },
            { value: 2, label: '2' },
            { value: 3, label: '3' },
            { value: 4, label: '4' },
            { value: 5, label: '5' },
        ];

        return {
            companies: locationsOptions,
            surveys: surveysOptions,
            statuses: statusesOptions,
            rating: ratingOptions,
        };
    }, [filter, coverManagerConnectedCompanies, isCoverManagerConnected]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                case 'hideAnonimous':
                    increment = filterChips[key] === true ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const updateCompanySensitiveData = newCompaniesIds => {
        const availableSurveys = getSurveysByLocationIds(newCompaniesIds);
        const isCoverManagerInCompanies = newCompaniesIds.some(item => coverManagerConnectedCompanies.includes(item));
        const surveys = [
            ...availableSurveys,
            ...(isCoverManagerConnected && isCoverManagerInCompanies ? [INCLUDE_COVER_MANAGER] : []),
            INCLUDE_RECOVERY_FORM,
        ];

        return { surveys };
    };

    const update = {
        companies: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];

            const isAllSurveysSelected = filter.surveys.length === options.surveys.length;
            const { surveys } = updateCompanySensitiveData(selectedValuesIds);

            setFilter(prev => ({
                ...prev,
                companies: selectedValues,
                surveys: updateState(prev?.surveys, surveys, isAllSurveysSelected),
            }));
        },
        surveys: value => setFilter(prev => ({ ...prev, surveys: value })),
        rating: value => setFilter(prev => ({ ...prev, rating: value })),
        statuses: value => setFilter(prev => ({ ...prev, statuses: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
        hideAnonimous: e => setFilter(prev => ({ ...prev, hideAnonimous: e.target.checked })),
    };

    const CHIPS_ACTIONS = {
        companies: {
            reset: () => update.companies(options.companies),
            value: getChipsLabel(filterChips?.companies),
        },
        surveys: {
            reset: () =>
                setFilter(prev => ({
                    ...prev,
                    surveys: updateCompanySensitiveData(prev.companies.map(c => c.id)).surveys,
                })),
            value: getChipsLabel(filterChips?.surveys),
        },
        rating: {
            reset: () => setFilter(prev => ({ ...prev, rating: [1, 2, 3, 4, 5] })),
            value: getChipsLabel(filterChips?.rating),
        },
        statuses: {
            reset: () => setFilter(prev => ({ ...prev, statuses: [0, 1, 2, 3] })),
            value: getChipsLabel(filterChips?.statuses),
        },
        timeframe: {
            reset: () => setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } })),
            value: filterChips?.timeframe?.start
                ? formatTimeframeChipValue(filterChips.timeframe.start, filterChips.timeframe.end, language)
                : '',
        },
        hideAnonimous: {
            reset: () => setFilter(prev => ({ ...prev, hideAnonimous: false })),
            value: filterChips?.hideAnonimous ? t('FeedbackResponses.hideAnonimous') : '',
        },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const resetFilters = () => Object.values(CHIPS_ACTIONS).forEach(({ reset }) => reset());

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (key === 'reviewId') continue;

            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: key === 'companies' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length && key !== 'surveys', // FIXME: hotfix for surveys
                });
            } else if (key === 'hideAnonimous') {
                formOptions.push({
                    type: 'switch',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    style: { gridColumn: isTablet ? '1 / span 1' : '1 / span 2' },
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key]) && key !== 'surveys') {
            // FIXME: hotfix for surveys
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    const hasDraftFilters = useMemo(
        () => !isValid || hasActiveChips(createUpdatedChips(filterChips, filter, options)),
        [isValid, filterChips, filter, options],
    );

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        isChipActive(filterChips[key]) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].reset();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </FiltersModal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
